import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import {
  Search, Plus, MapPin, Building2, Plane, GraduationCap,
  Camera, Landmark, Trophy, ShoppingBag, Home, Mail,
  TrafficCone, Layers, LayoutDashboard, Map as MapIcon,
  BarChart3, List, ArrowRight, X, AlertTriangle
} from 'lucide-react';

import { motion } from "framer-motion";
import { AppContext } from '../context/AppContext';
import { simplePostCall } from '../api/ApiServices';
import ApiConfig from '../api/ApiConfig';
import { MapContainer, TileLayer, Marker, Circle, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';


// Fix for default Leaflet marker icons in React
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
// --- CONFIGURATION ---
const GOOGLE_MAPS_API_KEY = "AIzaSyAmjmbacOzPJoVurUTAWhHi_DZZObxPsAU";


// --- STYLES (Inline for robustness) ---
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100vw',
    overflow: 'hidden',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    backgroundColor: '#f8fafc', // slate-50
    color: '#0f172a', // slate-900
    boxSizing: 'border-box'
  },
  header: {
    flexShrink: 0,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e2e8f0', // slate-200
    zIndex: 10,
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px'
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  logoBox: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    padding: '4px',
    backgroundColor: 'white'
  },
  tabsContainer: {
    display: 'flex',
    backgroundColor: '#f1f5f9', // slate-100
    padding: '4px',
    borderRadius: '8px',
    gap: '4px'
  },
  tabButton: (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    backgroundColor: isActive ? '#ffffff' : 'transparent',
    color: isActive ? '#4f46e5' : '#64748b', // indigo-600 vs slate-500
    boxShadow: isActive ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)' : 'none',
    transition: 'all 0.2s'
  }),
  controlsRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  searchBox: {
    position: 'relative',
    flex: '1 1 250px',
    maxWidth: '400px'
  },
  input: {
    width: '100%',
    padding: '8px 12px 8px 36px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box'
  },
  filterScroll: {
    display: 'flex',
    gap: '8px',
    overflowX: 'auto',
    paddingBottom: '2px',
    flex: 1
  },
  filterChip: (isActive, color) => ({
    padding: '6px 12px',
    borderRadius: '9999px',
    border: `1px solid ${isActive ? 'transparent' : '#e2e8f0'}`,
    backgroundColor: isActive ? '#1e293b' : '#ffffff', // slate-800 vs white
    color: isActive ? '#ffffff' : '#475569',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  }),
  mainContent: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#f8fafc'
  },
  viewContainer: {
    height: '100%',
    width: '100%',
    overflowY: 'auto',
    padding: '24px'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '16px'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    padding: '16px',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    display: 'flex',
    flexDirection: 'column'
  }
};


// --- DATA ---


const CATEGORIES = {
  Urban:       { id: 'Urban', label: 'Urban', icon: Building2, color: '#f3e8ff' }, // purple-100
  Transport:   { id: 'Transport', label: 'Transport', icon: Plane, color: '#dbeafe' }, // blue-100
  Education:   { id: 'Education', label: 'Education', icon: GraduationCap, color: '#ffedd5' }, // orange-100
  Tourist:     { id: 'Tourist', label: 'Tourist', icon: Camera, color: '#fce7f3' }, // pink-100
  Admin:       { id: 'Admin', label: 'Admin', icon: Landmark, color: '#f1f5f9' }, // slate-100
  Sports:      { id: 'Sports', label: 'Sports', icon: Trophy, color: '#d1fae5' }, // emerald-100
  Shopping:    { id: 'Shopping', label: 'Shopping', icon: ShoppingBag, color: '#cffafe' }, // cyan-100
  Residential: { id: 'Residential', label: 'Residential', icon: Home, color: '#ccfbf1' }, // teal-100
  Postal:      { id: 'Postal', label: 'Postal', icon: Mail, color: '#e0e7ff' }, // indigo-100
  Infra:       { id: 'Infra', label: 'Infrastructure', icon: TrafficCone, color: '#fee2e2' }, // red-100
};


const MOCK_DATA = [
  { id: 1, name: "Intl. Airport Terminal 1", category: "Transport", sub: "Airport", radius: 1.5, lat: 12.9900, lng: 77.7000 },
  { id: 2, name: "Central Business District", category: "Urban", sub: "Core Area", radius: 2.0, lat: 12.9716, lng: 77.5946 },
  { id: 3, name: "Grand Central Mall", category: "Shopping", sub: "Mall", radius: 0.3, lat: 12.9500, lng: 77.6200 },
  { id: 4, name: "City Stadium", category: "Sports", sub: "Stadium", radius: 0.5, lat: 12.9750, lng: 77.6300 },
  { id: 5, name: "Greenwood Residency", category: "Residential", sub: "Apartments", radius: 0.15, lat: 12.9300, lng: 77.5800 },
  { id: 6, name: "Main Post Office", category: "Postal", sub: "Head Office", radius: 0.1, lat: 12.9800, lng: 77.5900 },
  { id: 7, name: "University Campus", category: "Education", sub: "University", radius: 1.0, lat: 12.9200, lng: 77.6500 },
  { id: 8, name: "Historic Fort", category: "Tourist", sub: "Heritage", radius: 0.5, lat: 12.9600, lng: 77.5700 },
  { id: 9, name: "Highway Junction 42", category: "Infra", sub: "Junction", radius: 0.05, lat: 13.0100, lng: 77.5500 },
  { id: 10, name: "City Police HQ", category: "Admin", sub: "Police Station", radius: 0.1, lat: 12.9850, lng: 77.6000 },
  { id: 11, name: "North Bridge", category: "Infra", sub: "Bridge", radius: 0.5, lat: 13.0200, lng: 77.6100 },
  { id: 12, name: "Tech Park Zone", category: "Urban", sub: "Developing", radius: 5.0, lat: 12.8900, lng: 77.6800 },
];


// --- VIEWS ---
let DefaultIcon = L.icon({
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

// Sub-component to handle map panning when selectedId changes
function ChangeView({ center, zoom }) {
  const map = useMap();
  if (center[0] && center[1]) {
    map.flyTo(center, zoom, { duration: 1.5 });
  }
  return null;
}






const MapView = ({ pois, selectedId, onSelect, CATEGORIES }) => {
  const [mapError, setMapError] = useState(false);
  console.log("pois", pois);
  console.log("selectedId", selectedId);
  console.log("onSelect", onSelect);
  console.log("CATEGORIES", CATEGORIES);
  // Filter out invalid coordinates from API to prevent Leaflet crashes
  const validPois = pois.filter(
    (poi) => poi.lat && poi.lng
  );

  const selectedPoi = validPois.find((p) => p.id === selectedId);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <MapContainer
        center={[12.9716, 77.5946]} // Default center
        zoom={12}
        style={{ width: '100%', height: '100%' }}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Dynamic View Controller */}
        {selectedPoi && (
          <ChangeView 
            center={[parseFloat(selectedPoi.lat), parseFloat(selectedPoi.lng)]} 
            zoom={15} 
          />
        )}

        {validPois.map((poi) => {
          const position = [
            parseFloat(poi.lat),
            parseFloat(poi.lng),
          ];
          const isSelected = selectedId === poi.id;
          
          // Use radius in meters (location_radius_m) or fallback to 500m
          const radius = parseFloat(poi.radius) || 500;

          return (
            <React.Fragment key={poi.id}>
              <Marker 
                position={position}
                eventHandlers={{
                  click: () => onSelect(poi.id),
                }}
              >
                <Popup>
                  <strong>{poi.name || "Unnamed Location"}</strong>
                  <br />
                  Type: {poi.category}
                </Popup>
              </Marker>

              {/* Show Radius Circle */}
              <Circle
                center={position}
                radius={radius}
                pathOptions={{
                  fillColor: isSelected ? '#4f46e5' : '#94a3b8',
                  color: isSelected ? '#4f46e5' : '#94a3b8',
                  fillOpacity: 0.2,
                  weight: 2
                }}
              />
            </React.Fragment>
          );
        })}
      </MapContainer>

      {/* Floating Info Card (Overlay) */}
      {selectedPoi && (
        <div style={{
          position: 'absolute', bottom: '20px', left: '20px', right: '20px', zIndex: 1000,
          maxWidth: '400px', margin: '0 auto', backgroundColor: 'white',
          padding: '16px', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
          display: 'flex', gap: '12px', alignItems: 'center'
        }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '8px', 
            backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <MapPin size={20} color="#4f46e5" />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>
                {selectedPoi.location_name}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>
                Type: {selectedPoi.location_type} | Radius: {selectedPoi.location_radius_m || 0}m
            </p>
          </div>
          <button onClick={() => onSelect(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={20} color="#94a3b8" />
          </button>
        </div>
      )}
    </div>
  );
};
const ListView = ({ pois, onSelect, selectedId }) => {
  return (
    <div style={styles.viewContainer}>
      <div style={styles.grid}>
        {pois.map(poi => {
          const catConfig = CATEGORIES[poi.category] || CATEGORIES.Urban;
          const Icon = catConfig.icon;
          const isSelected = selectedId === poi.id;
          return (
            <div
              key={poi.id}
              onClick={() => onSelect(poi.id)}
              style={{
                ...styles.card,
                borderColor: isSelected ? '#6366f1' : '#e2e8f0',
                boxShadow: isSelected ? '0 0 0 2px #6366f1' : 'none'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ padding: '8px', borderRadius: '8px', backgroundColor: catConfig.color }}>
                  <Icon size={20} color="#334155" />
                </div>
                {isSelected && <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#6366f1' }} />}
              </div>
              <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: 'bold', color: '#0f172a' }}>{poi.name}</h3>
              <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>{poi.sub}</p>
              <div style={{ marginTop: 'auto', paddingTop: '12px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                 <span style={{ fontSize: '12px', padding: '2px 8px', borderRadius: '4px', backgroundColor: '#f1f5f9', color: '#475569' }}>R: {poi.radius} km</span>
                 <span style={{ fontSize: '12px', color: '#4f46e5', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>View <ArrowRight size={12}/></span>
              </div>
            </div>
          );
        })}
      </div>
      {pois.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
          <MapPin size={48} style={{ opacity: 0.2, margin: '0 auto 10px' }} />
          <p>No locations found.</p>
        </div>
      )}
    </div>
  );
};


const AnalyticsView = ({ data }) => {
  const total = data.length;
  const avgRadius = (data.reduce((acc, c) => acc + c.radius, 0) / (total || 1)).toFixed(2);
 const [categoryCounts, setCategoryCounts] = useState([]);
  return (
    <div >
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px', color: '#0f172a' }}>System Overview</h2>
       
        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <div style={styles.card}>
            <p style={{ margin: 0, fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Total POIs</p>
            <p style={{ margin: '8px 0 0', fontSize: '32px', fontWeight: 'bold', color: '#0f172a' }}>{total}</p>
          </div>
          <div style={styles.card}>
            <p style={{ margin: 0, fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Avg Radius</p>
            <p style={{ margin: '8px 0 0', fontSize: '32px', fontWeight: 'bold', color: '#4f46e5' }}>{avgRadius} <span style={{ fontSize: '16px', color: '#94a3b8', fontWeight: 'normal' }}>km</span></p>
          </div>
          <div style={styles.card}>
            <p style={{ margin: 0, fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Active Regions</p>
            <p style={{ margin: '8px 0 0', fontSize: '32px', fontWeight: 'bold', color: '#10b981' }}>12</p>
          </div>
        </div>


        {/* Category List */}
     {/* Category Distribution Section */}
<div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
  <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
    <BarChart3 size={20} color="#64748b" />
    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', color: '#0f172a' }}>Category Distribution</h3>
  </div>
  
  <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px 40px' }}>
    {categoryCounts.map((cat, index) => {
      // 1. Calculate Total for the progress bar percentage
      const totalCount = categoryCounts.reduce((acc, curr) => acc + parseInt(curr.count || 0), 0);
      
      // 2. Handle the "null" or empty categories from your API
      const typeKey = cat.location_type || 'Uncategorized';
      
      // 3. Get UI config (Using the CATEGORIES object from your state or a fallback)
      const catConfig = CATEGORIES[typeKey] || { 
        label: typeKey.replace('_', ' '), 
        icon: MapPin, 
        color: '#4f46e5' 
      };
      
      const Icon = catConfig.icon;
      const countValue = parseInt(cat.count || 0);
      const percentage = totalCount > 0 ? (countValue / totalCount) * 100 : 0;

      return (
        <div key={index}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '6px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#334155', fontWeight: 500 }}>
              <Icon size={16} color="#4f46e5" /> 
              <span style={{ textTransform: 'capitalize' }}>{catConfig.label}</span>
            </span>
            <span style={{ fontWeight: 'bold', color: '#0f172a' }}>{countValue}</span>
          </div>
          
          {/* Progress Bar */}
          <div style={{ width: '100%', height: '8px', backgroundColor: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${percentage}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              style={{ 
                height: '100%', 
                backgroundColor: '#4f46e5', 
                borderRadius: '4px' 
              }} 
            />
          </div>
          <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px', textAlign: 'right' }}>
            {percentage.toFixed(1)}% of total
          </div>
        </div>
      );
    })}
  </div>
</div>
      </div>
    </div>
  );
};


// --- MAIN CONTROLLER ---


export default function POIDashboard() {
const [currentView, setCurrentView] = useState('map');
  const [selectedId, setSelectedId] = useState(null);
  const [filterCat, setFilterCat] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // API STATE
  const [poiList, setPoiList] = useState([]);

  const [categoryCounts, setCategoryCounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const { sidebar } = useContext(AppContext);

  // 1. Fetch Categories for Analytics
  const fetchCategoryList = () => {
    let body = JSON.stringify({ isAnalytic: "true" });
    simplePostCall(ApiConfig.DASHBORDPOIcategory, body)
      .then((res) => {
        if (res.result && res.data) {
          setCategoryCounts(res.data);
        }
      })
      .catch(err => console.error("Category API Error:", err));
  };

  // 2. Fetch POI List for Map/List
  const fetchPoiList = () => {
    setIsLoading(true);
    let body = JSON.stringify({
      location_name: "",
      location_address: "",
      location_latitude: "",
      location_longitude: "",
      location_type: filterCat === 'All' ? "" : filterCat,
      page: 1,
      page_limit: 100
    });

    simplePostCall(ApiConfig.DASHBORDPOIFilter, body)
      .then((res) => {
   
        if (res.result && res.data) {
          // Map API fields to your component fields
          const formattedData = res.data.map(item => ({
            id: item.location_id || Math.random(),
            name: item.location_name || "Unknown Location",
            category: item.location_type || "Other",
            sub: item.location_address || "No address provided",
            radius: parseFloat(item.location_radius) || 0.5,
            lat: parseFloat(item.location_latitude),
            lng: parseFloat(item.location_longitude)
          }));
          setPoiList(formattedData);
        }
      })
      .catch(err => console.error("Filter API Error:", err))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchCategoryList();
    fetchPoiList();
  }, [filterCat]); // Refetch list when filter changes

  const filteredPOIs = useMemo(() => {
    return poiList.filter(poi => 
      poi.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      poi.sub.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [poiList, searchQuery]);
  
  const handleSelect = (id) => {
    setSelectedId(id);
    if (id) setCurrentView('map');
  };

const [formData, setFormData] = useState({
    location_name: '',
    location_address: '',
    location_latitude: '',
    location_longitude: '',
    location_radius_m: 50
  });

  // Handle Input Change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // API Call to Add POI
  const handleAddPOI = () => {
    setIsSubmitting(true);
    
    // Formatting payload based on your requirement
    const payload = JSON.stringify({
      location_name: formData.location_name,
      location_address: formData.location_address,
      // Mapping the "undefined" field from your example to the longitude
      "undefined": parseFloat(formData.location_longitude), 
      "drowvalue": [
        {
          "lat": parseFloat(formData.location_latitude),
          "lng": parseFloat(formData.location_longitude)
        }
      ],
      "location_radius_m": parseInt(formData.location_radius_m)
    });

    // Using your API service
    simplePostCall(ApiConfig.ADD_POI_ENDPOINT, payload)
      .then((data) => {
        if (data.result) {
          alert("POI Added Successfully!");
          setShowAddModal(false);
          setFormData({ location_name: '', location_address: '', location_latitude: '', location_longitude: '', location_radius_m: 50 });
          // Optional: Refresh your list
          // getcategorylFilter(); 
        } else {
          alert("Failed to add POI: " + (data.message || "Unknown error"));
        }
      })
      .catch((error) => {
        console.error("Add API Error:", error);
      })
      .finally(() => setIsSubmitting(false));
  };
  return (
  <motion.div
          className={sidebar && sidebar ? "taskMain " : "cx-active taskMain"}
          id="cx-main"
          
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.1 }}
          key={"motionAnimation"}
        >
            <div
              id="cx-wrapper"
              className="dashboard_responsive"
              key={"mainContainer"}
            >
              <div className="main-dashboard-wrapper">
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          {/* <div style={styles.brand}>
            <div style={styles.logoBox}>
               <img
                 src="image_be93db.png"
                 alt="Logo"
                 style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                 onError={(e) => {
                   e.target.style.display='none';
                   e.target.parentElement.style.backgroundColor = '#4f46e5';
                   e.target.parentElement.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>'
                 }}
               />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800 }}>FleetTrack</h1>
              <p style={{ margin: '2px 0 0', fontSize: '11px', fontWeight: 700, color: '#4f46e5', textTransform: 'uppercase', letterSpacing: '0.5px' }}>POI Manager</p>
            </div>
          </div> */}


          <div style={styles.controlsRow}>
            <div style={styles.tabsContainer}>
              {[
                { id: 'map', icon: MapIcon, label: 'Map' },
                { id: 'list', icon: List, label: 'List' },
                { id: 'analytics', icon: BarChart3, label: 'Analytics' }
              ].map(view => (
                <button
                  key={view.id}
                  onClick={() => setCurrentView(view.id)}
                  style={styles.tabButton(currentView === view.id)}
                >
                  <view.icon size={16} /> {view.label}
                </button>
              ))}
            </div>
            {/* <button style={{
              display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: '#0f172a', color: 'white',
              padding: '8px 16px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer'
            }}>
              <Plus size={14} /> Add POI
            </button> */}
          </div>
        </div>


        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', paddingTop: '8px' }}>
          <div style={styles.searchBox}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '10px', color: '#94a3b8' }} />
            <input
              type="text"
              placeholder="Search locations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={styles.input}
            />
          </div>
          <div style={styles.filterScroll}>
            {/* <button onClick={() => setFilterCat('All')} style={styles.filterChip(filterCat === 'All')}>All</button>
            {CATEGORIES.map(cat => (
              <button key={cat.id} onClick={() => setFilterCat(cat.id)} style={styles.filterChip(filterCat === cat.id, cat.color)}>
                {cat.label}
              </button>
            ))} */}
            {categoryCounts.map((cat, idx) => (
                    <button 
                      key={idx} 
                      onClick={() => setFilterCat(cat.location_type)} 
                      style={styles.filterChip(filterCat === cat.location_type)}
                    >
                      {cat.location_type}
                    </button>
                  ))}
          </div>
        </div>
      </div>


      {/* Main Content */}
      <div style={styles.mainContent}>
        {currentView === 'map' && (
          <MapView pois={filteredPOIs} selectedId={selectedId} onSelect={handleSelect} />
        )}
        {currentView === 'list' && (
          <ListView pois={filteredPOIs} selectedId={selectedId} onSelect={handleSelect} />
        )}
        {currentView === 'analytics' && (
          <AnalyticsView data={MOCK_DATA} />
        )}
      </div>
    </div>
    </div>
    </div>

    {/* <button 
        onClick={() => setShowAddModal(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: '#0f172a', color: 'white',
          padding: '8px 16px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer'
        }}
      >
        <Plus size={14} /> Add POI
      </button>*/}

      {/* MODAL COMPONENT */}
      {showAddModal && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.content}>
            <div style={modalStyles.header}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>Add New Point of Interest</h2>
              <button onClick={() => setShowAddModal(false)} style={modalStyles.closeBtn}><X size={20}/></button>
            </div>
            
            <div style={modalStyles.body}>
              <div style={modalStyles.field}>
                <label style={modalStyles.label}>Location Name</label>
                <input name="location_name" value={formData.location_name} onChange={handleInputChange} style={modalStyles.input} placeholder="e.g. Kanpur8" />
              </div>
              
              <div style={modalStyles.field}>
                <label style={modalStyles.label}>Full Address</label>
                <input name="location_address" value={formData.location_address} onChange={handleInputChange} style={modalStyles.input} placeholder="Enter street address" />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ ...modalStyles.field, flex: 1 }}>
                  <label style={modalStyles.label}>Latitude</label>
                  <input type="number" name="location_latitude" value={formData.location_latitude} onChange={handleInputChange} style={modalStyles.input} placeholder="26.46..." />
                </div>
                <div style={{ ...modalStyles.field, flex: 1 }}>
                  <label style={modalStyles.label}>Longitude</label>
                  <input type="number" name="location_longitude" value={formData.location_longitude} onChange={handleInputChange} style={modalStyles.input} placeholder="80.32..." />
                </div>
              </div>

              <div style={modalStyles.field}>
                <label style={modalStyles.label}>Radius (meters)</label>
                <input type="number" name="location_radius_m" value={formData.location_radius_m} onChange={handleInputChange} style={modalStyles.input} />
              </div>
            </div>

            <div style={modalStyles.footer}>
              <button onClick={() => setShowAddModal(false)} style={modalStyles.cancelBtn}>Cancel</button>
              <button 
                onClick={handleAddPOI} 
                disabled={isSubmitting}
                style={{
                  ...modalStyles.saveBtn,
                  opacity: isSubmitting ? 0.7 : 1
                }}
              >
                {isSubmitting ? 'Saving...' : 'Save Location'}
              </button>
            </div>
          </div>
        </div>
      )}
          </motion.div>
    
  );
}

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(4px)'
  },
  content: {
    backgroundColor: 'white',
    width: '90%',
    maxWidth: '500px',
    borderRadius: '12px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden'
  },
  header: {
    padding: '20px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
  },
  body: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' },
  footer: {
    padding: '16px 20px',
    backgroundColor: '#f8fafc',
    display: 'flex', justifyContent: 'flex-end', gap: '12px'
  },
  label: { fontSize: '13px', fontWeight: 600, color: '#475569', marginBottom: '6px', display: 'block' },
  input: {
    width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box'
  },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' },
  cancelBtn: { padding: '10px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: 'white', cursor: 'pointer' },
  saveBtn: { padding: '10px 16px', borderRadius: '8px', border: 'none', backgroundColor: '#4f46e5', color: 'white', fontWeight: 600, cursor: 'pointer' }
};